import React, { useCallback, useState, useEffect, useRef } from "react";
import { Box, IconButton } from "@mui/material";
import Div from "@jumbo/shared/Div";
import { Panel } from "react-resizable-panels";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DescriptionIcon from "@mui/icons-material/Description";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import HistoryIcon from "@mui/icons-material/History";
import DescriptionTab from "./DescriptionTab";
import VideoLabelIcon from "@mui/icons-material/VideoLabel";
import EditorialTab from "./EditorialTab";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SubmissionsTab from "./SubmissionsTab";
import compilerServices from "app/services/compiler-service";
import problemServices from "app/services/problem-service";
import { TabPanel } from "components/TabPanel";
import VideosTab from "./VideosTab";
import { StyledTab, StyledTabs } from "components/CustomTabs";
import { debounce } from "lodash";
import { HEIGHT_OFFSET } from "app/utils/constants/constants";
import VisualizationTab from "./VisualizationTab";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

const ContentPanel = ({
  problemData,
  loading,
  metadata,
  onSubmissionSelect,
  value,
  setValue,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [submissions, setSubmissions] = useState(null);
  const [submissionsLoaded, setSubmissionsLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMoreSubmissions, setHasMoreSubmissions] = useState(true);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [languageFilter, setLanguageFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const videoCacheRef = useRef(new Map());

  const panelRef = useRef(null);

  const { authUser } = useJumboAuth();
  const isLoggedIn = !!authUser;

  // Debounced handleResize to prevent flickering
  const handleResize = debounce((size) => {
    if (panelRef.current) {
      if (isPanelOpen && size <= 5) {
        // Close the panel if resizing to or below collapsed size
        setIsPanelOpen(false);
        panelRef.current.collapse();
      } else if (!isPanelOpen && size > 5) {
        // Open the panel if resizing beyond collapsed size
        setIsPanelOpen(true);
        panelRef.current.expand();
      }
    }
  }, 0);

  // Toggle panel visibility
  const togglePanel = useCallback(() => {
    if (panelRef.current) {
      if (isPanelOpen) {
        panelRef.current.collapse();
        setIsPanelOpen(false);
      } else {
        panelRef.current.expand();
        panelRef.current.resize(50);
        setIsPanelOpen(true);
      }
    }
  }, [isPanelOpen]);

  // Fetch submissions for a given page
  const fetchSubmissions = useCallback(
    async (pageNumber, allowFetchRegardless = false) => {
      if (!problemData?.id || (!allowFetchRegardless && !hasMoreSubmissions)) {
        setSubmissionsLoaded(true);
        return;
      }
      setSubmissionsLoaded(false);
      try {
        const res = await compilerServices.getUserProblemSubmissions(
          problemData.id,
          pageNumber
        );
        const submissionsArray = Array.isArray(res) ? res : [];
        setSubmissions((prev) => [...(prev || []), ...submissionsArray]);
        setHasMoreSubmissions(submissionsArray.length === 20);
        setPage(pageNumber);
      } catch (error) {
        console.error("Error fetching submissions:", error);
      } finally {
        setSubmissionsLoaded(true);
      }
    },
    [problemData?.id, hasMoreSubmissions]
  );

  // Fetch video data only when VideosTab is first selected
  const fetchVideos = useCallback(async (problemName) => {
    if (videoCacheRef.current.has(problemName)) {
      setVideoData(videoCacheRef.current.get(problemName));
      return;
    }
    if (!problemName) return;

    setVideoLoading(true);
    try {
      const videos = await problemServices.getVideoUrl(problemName);
      videoCacheRef.current.set(problemName, videos);
      setVideoData(videos);
    } catch (error) {
      console.error(`Error fetching videos for ${problemName}:`, error);
      setVideoData([]);
    } finally {
      setVideoLoading(false);
    }
  }, []);

  // Handle tab change
  const handleTabChange = useCallback(
    (event, newValue) => {
      setValue(newValue);
      if (!isPanelOpen) {
        setIsPanelOpen(true);
        if (panelRef.current) {
          panelRef.current.expand();
          panelRef.current.resize(50);
        }
      }
      if (newValue === 2 && problemData?.name) {
        // Fetch videos only for VideosTab
        fetchVideos(problemData.name);
      }
      if (newValue === 4 && submissions === null) {
        fetchSubmissions(0);
      }
    },
    [setValue, isPanelOpen, problemData.name, submissions, fetchVideos, fetchSubmissions]
  );

  // Fetch videos and submissions when problem or tab changes
  useEffect(() => {
    if (value === 2 && problemData?.name && !videoData) {
      fetchVideos(problemData.name);
    }
    if (value === 4 && problemData?.id && submissions === null) {
      fetchSubmissions(0);
    }
  }, [
    value,
    problemData,
    submissions,
    videoData,
    fetchSubmissions,
    fetchVideos,
  ]);

  const handleStatusClick = (event) => setStatusAnchorEl(event.currentTarget);
  const handleLanguageClick = (event) =>
    setLanguageAnchorEl(event.currentTarget);
  const handleClose = () => {
    setStatusAnchorEl(null);
    setLanguageAnchorEl(null);
  };

  const handleStatusSelect = (status) => {
    setStatusFilter(status === "All Statuses" ? null : status);
    handleClose();
  };

  const handleLanguageSelect = (language) => {
    setLanguageFilter(language === "All Languages" ? null : language);
    handleClose();
  };

  const handleRefresh = () => {
    if (!problemData || !problemData.id) return;
    setSubmissions([]);
    setPage(0);
    setHasMoreSubmissions(true);
    setLanguageFilter(null);
    setStatusFilter(null);
    fetchSubmissions(0, true);
  };

  return (
    <Panel
      ref={panelRef}
      defaultSize={50}
      minSize={5}
      collapsedSize={5}
      collapsible
      onCollapse={(collapsed) => setIsPanelOpen(!collapsed)}
      onResize={handleResize}
    >
      {isPanelOpen ? (
        <Div
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Div
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
            }}
          >
            <StyledTabs
              value={value}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flexGrow: 1 }}
            >
              <StyledTab
                icon={<DescriptionIcon />}
                iconPosition="start"
                label="Description"
                isPanelOpen={isPanelOpen}
              />
              <StyledTab
                icon={<ImportContactsIcon />}
                iconPosition="start"
                label="Editorial"
                isPanelOpen={isPanelOpen}
              />
              <StyledTab
                icon={<VideoLabelIcon />}
                iconPosition="start"
                label="Videos"
                isPanelOpen={isPanelOpen}
              />
              <StyledTab
                icon={<ShowChartIcon />}
                iconPosition="start"
                label="Visualizations"
                isPanelOpen={isPanelOpen}
              />
              <StyledTab
                icon={<HistoryIcon />}
                iconPosition="start"
                label="Submissions"
                isPanelOpen={isPanelOpen}
              />
            </StyledTabs>
            <IconButton onClick={togglePanel}>
              <ChevronLeftIcon />
            </IconButton>
          </Div>
          <TabPanel value={value} index={0} sx={{ p: 0 }}>
            <DescriptionTab problemData={problemData} />
          </TabPanel>
          <TabPanel value={value} index={1} sx={{ p: 0 }}>
            <EditorialTab
              problemData={problemData}
              loading={loading}
              isPanelOpen={isPanelOpen}
            />
          </TabPanel>
          <TabPanel
            value={value}
            index={2}
            sx={{
              p: 0,
              minWidth: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <VideosTab isLoading={videoLoading} videos={videoData} />
          </TabPanel>
          <TabPanel value={value} index={3} sx={{ p: 0 }}>
            <VisualizationTab problemData={problemData} />
          </TabPanel>
          <TabPanel value={value} index={4} sx={{ p: 0 }}>
            <SubmissionsTab
              isLoggedIn={isLoggedIn}
              submissions={submissions}
              submissionsLoaded={submissionsLoaded}
              statusAnchorEl={statusAnchorEl}
              languageAnchorEl={languageAnchorEl}
              languageFilter={languageFilter}
              statusFilter={statusFilter}
              handleStatusClick={handleStatusClick}
              handleLanguageClick={handleLanguageClick}
              handleClose={handleClose}
              handleStatusSelect={handleStatusSelect}
              handleLanguageSelect={handleLanguageSelect}
              metadata={metadata}
              onSubmissionSelect={onSubmissionSelect}
              fetchMoreSubmissions={() => fetchSubmissions(page + 1)}
              hasMoreSubmissions={hasMoreSubmissions}
              handleRefresh={handleRefresh}
            />
          </TabPanel>
        </Div>
      ) : (
        <Div
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <StyledTabs
            value={value}
            onChange={handleTabChange}
            orientation="vertical"
            variant="scrollable"
            scrollButtons="auto"
            isPanelOpen={isPanelOpen}
            sx={{
              height: `calc(100vh - ${HEIGHT_OFFSET.DEFAULT})`,
            }}
          >
            <StyledTab
              icon={<DescriptionIcon />}
              iconPosition="start"
              label="Description"
              isPanelOpen={isPanelOpen}
            />
            <StyledTab
              icon={<ImportContactsIcon />}
              iconPosition="start"
              label="Editorial"
              isPanelOpen={isPanelOpen}
            />
            <StyledTab
              icon={<VideoLabelIcon />}
              iconPosition="start"
              label="Videos"
              isPanelOpen={isPanelOpen}
              sx={{
                marginTop: "-12px",
              }}
            />
            <StyledTab
              icon={<ShowChartIcon />}
              iconPosition="start"
              label="Visualizations"
              isPanelOpen={isPanelOpen}
              sx={{
                marginTop: "10px",
                marginBottom: "24px",
              }}
            />
            <StyledTab
              icon={<HistoryIcon />}
              iconPosition="start"
              label="Submissions"
              isPanelOpen={isPanelOpen}
            />
          </StyledTabs>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <IconButton onClick={togglePanel}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Div>
      )}
    </Panel>
  );
};

export default ContentPanel;
